/**
 * Ragdoll stickman per SDD §4.4.
 * Composition: head + torso + 2 arms (upper + forearm) + 2 legs (upper + lower).
 * Joints are Matter constraints; soft "muscle" springs keep limbs near rest pose
 * so the figure doesn't go fully limp.
 *
 * Hook attaches at the tip of one of the hands (whichever is nearer the target).
 */

import Matter from 'matter-js';
import { Container, Graphics } from 'pixi.js';
import { getActiveSkin, HAT_COLORS, GLOVES_COLORS, BOOTS_COLORS, type ActiveSkin } from './PlayerSkin.js';

type PartKind =
  | { kind: 'torso'; w: number; h: number }
  | { kind: 'head'; r: number }
  | { kind: 'limb'; w: number; h: number }
  | { kind: 'forearm'; w: number; h: number; side: 'L' | 'R' }
  | { kind: 'lowerLeg'; w: number; h: number; side: 'L' | 'R' };

interface PartDef {
  body: Matter.Body;
  view: Graphics;
  shape: PartKind;
}

export class Ragdoll {
  readonly composite: Matter.Composite;
  readonly view: Container;
  readonly torso: Matter.Body;
  readonly head: Matter.Body;
  readonly leftHand: Matter.Body;
  readonly rightHand: Matter.Body;
  /** Cached set of all bodies for O(1) membership tests in collision loops. */
  readonly bodySet: Set<Matter.Body> = new Set();
  /** Cached array equivalent (same identities, frozen after construction). */
  readonly bodyList: Matter.Body[] = [];

  private readonly parts: PartDef[] = [];
  private readonly muscles: Matter.Constraint[] = [];
  /** Per-part snapshot of body x/y/angle taken just before each physics step,
   *  so the renderer can interpolate between two physics states and avoid the
   *  120 Hz-vs-60 Hz strobing aliasing when moving fast. Index-aligned with
   *  this.parts. */
  private readonly prev: Array<{ x: number; y: number; angle: number }> = [];
  /** Last interpolated transform written to each body's view, indexed by body.
   *  Hook.render uses this so the rope endpoint stays glued to the rendered
   *  hand instead of the raw (un-interpolated) Matter body. */
  private readonly displayByBody: Map<Matter.Body, { x: number; y: number; sin: number; cos: number; angle: number }> = new Map();
  private displayTorsoX = 0;
  private displayTorsoY = 0;
  private headGfx!: Graphics;
  /** Latched facing direction: +1 right, -1 left. */
  private facing: 1 | -1 = 1;
  /** Last mood/facing rendered, so we skip redrawing the face when nothing changed. */
  private lastDrawnMood: string = '';
  private lastDrawnFacing: 1 | -1 = 1;
  /** Active visual skin (palette + cosmetics). Resolved on construction
   *  and re-applied via `applySkin()` whenever the user picks a new look. */
  private skin: ActiveSkin = getActiveSkin();
  /** Cosmetic overlay graphics. Each is a Graphics added directly to
   *  `this.view` (the same container as parts) so it renders on top of
   *  every body part with no clipping. Each cosmetic carries the body
   *  it should follow so `sync()` can place + rotate it like a part. */
  private cosmetics: Array<{ body: Matter.Body; view: Graphics }> = [];
  /** External 'mood' input set by GameScene each frame. */
  mood: 'idle' | 'hooked' | 'falling' | 'hurt' | 'happy' = 'idle';

  constructor(x: number, y: number) {
    // Negative shared group → no collisions between own parts.
    const group = Matter.Body.nextGroup(true);
    const filter = { group, category: 0x0002, mask: 0xffff };

    this.view = new Container();
    this.composite = Matter.Composite.create({ label: 'ragdoll' });

    // ---- Bodies (positions are local layout, then translated by spawn) ----
    // Funky physics knobs: near-zero air drag preserves swing energy across
    // long pendulum arcs; small restitution keeps platform contacts lively.
    const AIR = 0.0008;
    const REST = 0.15;

    const torso = Matter.Bodies.rectangle(x, y, 14, 26, {
      density: 0.0018,
      collisionFilter: filter,
      frictionAir: AIR,
      friction: 0.02,
      restitution: REST,
      label: 'ragdoll-torso',
    });
    const head = Matter.Bodies.circle(x, y - 24, 9, {
      density: 0.0012,
      collisionFilter: filter,
      frictionAir: AIR,
      friction: 0.02,
      restitution: REST,
      label: 'ragdoll-head',
    });

    // Arms: shoulder at top of torso, hang down by default.
    const upperArmL = Matter.Bodies.rectangle(x - 10, y - 6, 6, 16, {
      density: 0.0010,
      collisionFilter: filter,
      frictionAir: AIR,
      friction: 0.02,
      restitution: REST,
      label: 'ragdoll-upperArmL',
    });
    const forearmL = Matter.Bodies.rectangle(x - 10, y + 10, 6, 16, {
      density: 0.0010,
      collisionFilter: filter,
      frictionAir: AIR,
      friction: 0.02,
      restitution: REST,
      label: 'ragdoll-forearmL',
    });
    const upperArmR = Matter.Bodies.rectangle(x + 10, y - 6, 6, 16, {
      density: 0.0010,
      collisionFilter: filter,
      frictionAir: AIR,
      friction: 0.02,
      restitution: REST,
      label: 'ragdoll-upperArmR',
    });
    const forearmR = Matter.Bodies.rectangle(x + 10, y + 10, 6, 16, {
      density: 0.0010,
      collisionFilter: filter,
      frictionAir: AIR,
      friction: 0.02,
      restitution: REST,
      label: 'ragdoll-forearmR',
    });

    // Legs: hips at bottom of torso.
    const upperLegL = Matter.Bodies.rectangle(x - 5, y + 22, 7, 18, {
      density: 0.0012,
      collisionFilter: filter,
      frictionAir: AIR,
      friction: 0.02,
      restitution: REST,
      label: 'ragdoll-upperLegL',
    });
    const lowerLegL = Matter.Bodies.rectangle(x - 5, y + 40, 7, 18, {
      density: 0.0012,
      collisionFilter: filter,
      frictionAir: AIR,
      friction: 0.02,
      restitution: REST,
      label: 'ragdoll-lowerLegL',
    });
    const upperLegR = Matter.Bodies.rectangle(x + 5, y + 22, 7, 18, {
      density: 0.0012,
      collisionFilter: filter,
      frictionAir: AIR,
      friction: 0.02,
      restitution: REST,
      label: 'ragdoll-upperLegR',
    });
    const lowerLegR = Matter.Bodies.rectangle(x + 5, y + 40, 7, 18, {
      density: 0.0012,
      collisionFilter: filter,
      frictionAir: AIR,
      friction: 0.02,
      restitution: REST,
      label: 'ragdoll-lowerLegR',
    });

    this.torso = torso;
    this.head = head;
    this.leftHand = forearmL;
    this.rightHand = forearmR;

    // ---- Joints (Matter constraints) ----
    const joints: Matter.Constraint[] = [
      // Neck: two parallel constraints form a stiff hinge so the head sits
      // upright on the torso instead of flopping around a single pivot.
      Matter.Constraint.create({
        bodyA: torso, pointA: { x: -3, y: -13 },
        bodyB: head, pointB: { x: -3, y: 9 },
        stiffness: 0.9, length: 2,
      }),
      Matter.Constraint.create({
        bodyA: torso, pointA: { x: 3, y: -13 },
        bodyB: head, pointB: { x: 3, y: 9 },
        stiffness: 0.9, length: 2,
      }),
      // Shoulders.
      Matter.Constraint.create({
        bodyA: torso, pointA: { x: -7, y: -10 },
        bodyB: upperArmL, pointB: { x: 0, y: -8 },
        stiffness: 0.7, length: 0,
      }),
      Matter.Constraint.create({
        bodyA: torso, pointA: { x: 7, y: -10 },
        bodyB: upperArmR, pointB: { x: 0, y: -8 },
        stiffness: 0.7, length: 0,
      }),
      // Elbows.
      Matter.Constraint.create({
        bodyA: upperArmL, pointA: { x: 0, y: 8 },
        bodyB: forearmL, pointB: { x: 0, y: -8 },
        stiffness: 0.7, length: 0,
      }),
      Matter.Constraint.create({
        bodyA: upperArmR, pointA: { x: 0, y: 8 },
        bodyB: forearmR, pointB: { x: 0, y: -8 },
        stiffness: 0.7, length: 0,
      }),
      // Hips.
      Matter.Constraint.create({
        bodyA: torso, pointA: { x: -4, y: 13 },
        bodyB: upperLegL, pointB: { x: 0, y: -9 },
        stiffness: 0.7, length: 0,
      }),
      Matter.Constraint.create({
        bodyA: torso, pointA: { x: 4, y: 13 },
        bodyB: upperLegR, pointB: { x: 0, y: -9 },
        stiffness: 0.7, length: 0,
      }),
      // Knees.
      Matter.Constraint.create({
        bodyA: upperLegL, pointA: { x: 0, y: 9 },
        bodyB: lowerLegL, pointB: { x: 0, y: -9 },
        stiffness: 0.7, length: 0,
      }),
      Matter.Constraint.create({
        bodyA: upperLegR, pointA: { x: 0, y: 9 },
        bodyB: lowerLegR, pointB: { x: 0, y: -9 },
        stiffness: 0.7, length: 0,
      }),
    ];

    // Soft pose-keeping springs: keep arms / legs near torso so the figure
    // doesn't flap chaotically. Low stiffness, long-ish length.
    const muscles: Matter.Constraint[] = [
      Matter.Constraint.create({
        bodyA: torso, pointA: { x: -4, y: 13 },
        bodyB: lowerLegL, pointB: { x: 0, y: 0 },
        stiffness: 0.02, damping: 0.1, length: 38,
      }),
      Matter.Constraint.create({
        bodyA: torso, pointA: { x: 4, y: 13 },
        bodyB: lowerLegR, pointB: { x: 0, y: 0 },
        stiffness: 0.02, damping: 0.1, length: 38,
      }),
      Matter.Constraint.create({
        bodyA: torso, pointA: { x: -7, y: -10 },
        bodyB: forearmL, pointB: { x: 0, y: 0 },
        stiffness: 0.015, damping: 0.1, length: 30,
      }),
      Matter.Constraint.create({
        bodyA: torso, pointA: { x: 7, y: -10 },
        bodyB: forearmR, pointB: { x: 0, y: 0 },
        stiffness: 0.015, damping: 0.1, length: 30,
      }),
    ];
    this.muscles.push(...muscles);

    Matter.Composite.add(this.composite, [
      torso, head,
      upperArmL, forearmL, upperArmR, forearmR,
      upperLegL, lowerLegL, upperLegR, lowerLegR,
      ...joints, ...muscles,
    ]);

    // ---- Visuals (one Graphics per part; the per-part draw style comes
    // from the active skin and can be live-swapped via applySkin()). ----
    const addPart = (body: Matter.Body, shape: PartKind): Graphics => {
      const g = new Graphics();
      this.view.addChild(g);
      this.parts.push({ body, view: g, shape });
      return g;
    };

    addPart(torso, { kind: 'torso', w: 14, h: 26 });
    this.headGfx = addPart(head, { kind: 'head', r: 9 });
    addPart(upperArmL, { kind: 'limb', w: 6, h: 16 });
    addPart(forearmL,  { kind: 'forearm', w: 6, h: 16, side: 'L' });
    addPart(upperArmR, { kind: 'limb', w: 6, h: 16 });
    addPart(forearmR,  { kind: 'forearm', w: 6, h: 16, side: 'R' });
    addPart(upperLegL, { kind: 'limb', w: 7, h: 18 });
    addPart(lowerLegL, { kind: 'lowerLeg', w: 7, h: 18, side: 'L' });
    addPart(upperLegR, { kind: 'limb', w: 7, h: 18 });
    addPart(lowerLegR, { kind: 'lowerLeg', w: 7, h: 18, side: 'R' });

    // Paint everything (palette + cosmetic overlays).
    this.applySkin(this.skin);

    // Snapshot bodies once so per-frame collision handlers can do O(1)
    // membership tests without re-walking the composite each event.
    for (const p of this.parts) {
      this.bodyList.push(p.body);
      this.bodySet.add(p.body);
    }
  }

  /** Pick which hand is closer to a target world point. */
  pickHookHand(targetX: number, targetY: number): Matter.Body {
    const lx = this.leftHand.position.x - targetX;
    const ly = this.leftHand.position.y - targetY;
    const rx = this.rightHand.position.x - targetX;
    const ry = this.rightHand.position.y - targetY;
    return lx * lx + ly * ly <= rx * rx + ry * ry ? this.leftHand : this.rightHand;
  }

  /** Local offset on the hand body where the rope attaches (hand tip). */
  static handAttachOffset(): { x: number; y: number } {
    return { x: 0, y: 8 };
  }

  /** Sync all sprite transforms from physics, and redraw the dynamic face.
   *  When `alpha` is given (0..1), the rendered transforms are linearly
   *  interpolated between the last snapshotPrev() and the live physics
   *  state. This decouples render rate from the 60 Hz fixed step so high
   *  refresh-rate displays don't strobe between two stale frames. */
  sync(alpha = 1): void {
    const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    for (let i = 0; i < this.parts.length; i++) {
      const p = this.parts[i]!;
      const prev = this.prev[i];
      let x: number, y: number, ang: number;
      if (prev && a < 1) {
        x = prev.x + (p.body.position.x - prev.x) * a;
        y = prev.y + (p.body.position.y - prev.y) * a;
        ang = prev.angle + shortestAngleDelta(prev.angle, p.body.angle) * a;
      } else {
        x = p.body.position.x;
        y = p.body.position.y;
        ang = p.body.angle;
      }
      p.view.x = x;
      p.view.y = y;
      p.view.rotation = ang;
      const cached = this.displayByBody.get(p.body);
      const sin = Math.sin(ang);
      const cos = Math.cos(ang);
      if (cached) { cached.x = x; cached.y = y; cached.sin = sin; cached.cos = cos; cached.angle = ang; }
      else this.displayByBody.set(p.body, { x, y, sin, cos, angle: ang });
      if (p.body === this.torso) { this.displayTorsoX = x; this.displayTorsoY = y; }
    }
    // Cosmetics ride along with their host body using the same
    // interpolated transforms cached above.
    for (const c of this.cosmetics) {
      const t = this.displayByBody.get(c.body);
      if (!t) continue;
      c.view.x = t.x;
      c.view.y = t.y;
      c.view.rotation = t.angle;
    }
    this.drawFace();
  }

  /** Snapshot the current physics state so the next sync(alpha) can
   *  interpolate from it. Call this immediately before each Engine.update. */
  snapshotPrev(): void {
    for (let i = 0; i < this.parts.length; i++) {
      const b = this.parts[i]!.body;
      const cur = this.prev[i];
      if (cur) { cur.x = b.position.x; cur.y = b.position.y; cur.angle = b.angle; }
      else this.prev[i] = { x: b.position.x, y: b.position.y, angle: b.angle };
    }
  }

  /** Interpolated render-space transform for a ragdoll body. Returns null if
   *  the body isn't part of this ragdoll. Matches the last sync() call. */
  displayTransform(body: Matter.Body): { x: number; y: number; sin: number; cos: number; angle: number } | null {
    return this.displayByBody.get(body) ?? null;
  }

  /** Render-space (interpolated) torso position. Used by the camera so it
   *  follows the *visible* player, not the stale physics torso. */
  get displayPosition(): { x: number; y: number } {
    return { x: this.displayTorsoX, y: this.displayTorsoY };
  }

  /** Side-view face: one eye + a small mouth, drawn in head-local coords.
   *  We follow the direction of horizontal motion so the character looks
   *  toward where they're going. */
  private drawFace(): void {
    // Update facing latch based on horizontal motion before the early-out so
    // the cache key reflects the current intent.
    const vx = this.torso.velocity.x;
    if (vx > 0.5) this.facing = 1;
    else if (vx < -0.5) this.facing = -1;
    if (this.mood === this.lastDrawnMood && this.facing === this.lastDrawnFacing) return;
    this.lastDrawnMood = this.mood;
    this.lastDrawnFacing = this.facing;
    const g = this.headGfx;
    const r = 9;
    const skinColor = this.skin.preset.colors.skin;
    const ink = this.skin.preset.colors.stroke;
    g.clear();
    // Skin
    g.circle(0, 0, r).fill(skinColor).stroke({ color: ink, width: 2 });

    const f = this.facing;

    // Eye position (side view, so just one eye on the facing side).
    const eyeX = 3 * f;
    const eyeY = -1.8;

    if (this.mood === 'hurt') {
      // X eye
      g.moveTo(eyeX - 2, eyeY - 2).lineTo(eyeX + 2, eyeY + 2)
        .moveTo(eyeX + 2, eyeY - 2).lineTo(eyeX - 2, eyeY + 2)
        .stroke({ color: ink, width: 1.6 });
    } else if (this.mood === 'falling') {
      // Wide round eye
      g.circle(eyeX, eyeY, 2.4).fill(0xffffff).stroke({ color: ink, width: 1 });
      g.circle(eyeX, eyeY + 0.6, 1.2).fill(ink);
    } else if (this.mood === 'hooked') {
      // Determined slit
      g.moveTo(eyeX - 2.5, eyeY).lineTo(eyeX + 2.5, eyeY)
        .stroke({ color: ink, width: 1.8 });
    } else if (this.mood === 'happy') {
      // Closed smiling eye  ^
      g.moveTo(eyeX - 2.5, eyeY + 0.5).quadraticCurveTo(eyeX, eyeY - 1.8, eyeX + 2.5, eyeY + 0.5)
        .stroke({ color: ink, width: 1.5 });
    } else {
      // Idle: small dot
      g.circle(eyeX, eyeY, 1.3).fill(ink);
    }

    // Mouth on the facing side, slightly forward of center.
    const mx = 1.5 * f;
    if (this.mood === 'happy') {
      g.moveTo(mx - 2.5, 3).quadraticCurveTo(mx, 6, mx + 2.5, 3)
        .stroke({ color: ink, width: 1.5 });
    } else if (this.mood === 'falling' || this.mood === 'hurt') {
      g.circle(mx, 3.5, 1.6).fill(ink);
    } else if (this.mood === 'hooked') {
      g.moveTo(mx - 2.5, 3.5).lineTo(mx + 2.5, 3.5)
        .stroke({ color: ink, width: 1.5 });
    } else {
      g.moveTo(mx - 1.8, 3.5).lineTo(mx + 1.8, 3.5)
        .stroke({ color: ink, width: 1.2 });
    }
  }

  /** Re-apply a skin (palette + cosmetics) to all part graphics. Safe to
   *  call any time after construction; cheap (just clears + redraws each
   *  Graphics, no body rebuild). The face is forced to redraw on the next
   *  sync() by clearing the mood/facing cache key. */
  applySkin(skin: ActiveSkin): void {
    this.skin = skin;
    const colors = skin.preset.colors;
    const stroke = { color: colors.stroke, width: 2 } as const;

    // Detach + clear previous cosmetic overlays.
    for (const c of this.cosmetics) {
      c.view.parent?.removeChild(c.view);
      c.view.destroy();
    }
    this.cosmetics.length = 0;

    for (const p of this.parts) {
      const g = p.view;
      g.clear();
      const s = p.shape;
      if (s.kind === 'head') {
        // Head fill happens in drawFace(); here we just paint a base so
        // the very first frame (before sync) isn't blank if anything peeks.
        g.circle(0, 0, s.r).fill(colors.skin).stroke(stroke);
      } else if (s.kind === 'torso') {
        g.roundRect(-s.w / 2, -s.h / 2, s.w, s.h, Math.min(s.w, s.h) / 2)
          .fill(colors.torso).stroke(stroke);
      } else {
        g.roundRect(-s.w / 2, -s.h / 2, s.w, s.h, Math.min(s.w, s.h) / 2)
          .fill(colors.limb).stroke(stroke);
      }
    }

    // Cosmetics: drawn as independent Graphics added to the main
    // container. Each cosmetic remembers the body it tracks so sync()
    // can apply the per-frame interpolated transform — same as parts —
    // which avoids the headache of nesting Graphics inside Graphics.
    const headPart = this.parts.find(p => p.shape.kind === 'head');
    if (headPart && skin.cosmetics.hat) {
      const hat = new Graphics();
      this.drawHat(hat, skin.cosmetics.hat, colors.stroke);
      this.view.addChild(hat);
      this.cosmetics.push({ body: headPart.body, view: hat });
    }
    for (const p of this.parts) {
      if (p.shape.kind === 'forearm' && skin.cosmetics.gloves) {
        const glove = new Graphics();
        this.drawGlove(glove, skin.cosmetics.gloves, colors.stroke);
        this.view.addChild(glove);
        this.cosmetics.push({ body: p.body, view: glove });
      } else if (p.shape.kind === 'lowerLeg' && skin.cosmetics.boots) {
        const boot = new Graphics();
        this.drawBoot(boot, skin.cosmetics.boots, colors.stroke);
        this.view.addChild(boot);
        this.cosmetics.push({ body: p.body, view: boot });
      }
    }

    // Force the face to redraw on next sync() (palette/ink may have changed).
    this.lastDrawnMood = '';
  }

  private drawHat(g: Graphics, id: import('./PlayerSkin.js').HatId, ink: number): void {
    const stroke = { color: ink, width: 1.4 } as const;
    const top = -9; // head radius
    const c = HAT_COLORS[id];
    if (id === 'cap') {
      // Brim + crown
      g.rect(-7, top - 1, 14, 2).fill(c.fill).stroke(stroke);
      g.roundRect(-5, top - 7, 10, 6, 2).fill(c.fill).stroke(stroke);
      // Visor extends forward (use facing-agnostic symmetric shape)
      g.rect(2, top - 1, 6, 2).fill(c.fill).stroke(stroke);
    } else if (id === 'beanie') {
      g.roundRect(-7, top - 6, 14, 7, 3).fill(c.fill).stroke(stroke);
      if (c.band !== undefined) g.rect(-7, top - 1, 14, 2).fill(c.band).stroke(stroke);
      g.circle(0, top - 7, 1.6).fill(c.band ?? c.fill).stroke(stroke);
    } else if (id === 'tophat') {
      g.rect(-9, top - 1, 18, 2).fill(c.fill).stroke(stroke);
      g.rect(-5, top - 11, 10, 10).fill(c.fill).stroke(stroke);
      if (c.band !== undefined) g.rect(-5, top - 4, 10, 1.6).fill(c.band);
    } else if (id === 'crown') {
      g.rect(-7, top - 1, 14, 2).fill(c.fill).stroke(stroke);
      // Three points
      g.poly([-7, top - 1, -5, top - 7, -3, top - 1]).fill(c.fill).stroke(stroke);
      g.poly([-1.5, top - 1, 0, top - 8, 1.5, top - 1]).fill(c.fill).stroke(stroke);
      g.poly([3, top - 1, 5, top - 7, 7, top - 1]).fill(c.fill).stroke(stroke);
      if (c.band !== undefined) g.circle(0, top - 6, 1.4).fill(c.band);
    } else if (id === 'horns') {
      // Two small curved horns sprouting up-out
      g.poly([-5, top, -7, top - 6, -3, top - 1]).fill(c.fill).stroke(stroke);
      g.poly([5, top, 7, top - 6, 3, top - 1]).fill(c.fill).stroke(stroke);
    }
  }

  private drawGlove(g: Graphics, id: import('./PlayerSkin.js').GlovesId, ink: number): void {
    const stroke = { color: ink, width: 1.2 } as const;
    const fill = GLOVES_COLORS[id];
    // Forearm local: y=+8 is the hand tip. Draw a circle there.
    g.circle(0, 8, 4).fill(fill).stroke(stroke);
  }

  private drawBoot(g: Graphics, id: import('./PlayerSkin.js').BootsId, ink: number): void {
    const stroke = { color: ink, width: 1.2 } as const;
    const fill = BOOTS_COLORS[id];
    // Lower leg local: y=+9 is the foot. Draw a small boot shape
    // (rounded rect extending forward + sole).
    g.roundRect(-4, 6, 10, 7, 2).fill(fill).stroke(stroke);
    g.rect(-4, 12, 10, 2).fill(ink);
  }

  /** Used by the camera + off-world check. */
  get position(): Matter.Vector {
    return this.torso.position;
  }

  /** All bodies, for collision/contact lookups. Returns the cached array. */
  allBodies(): Matter.Body[] {
    return this.bodyList;
  }

  setTransform(x: number, y: number): void {
    const dx = x - this.torso.position.x;
    const dy = y - this.torso.position.y;
    Matter.Composite.translate(this.composite, { x: dx, y: dy });
    for (const b of this.allBodies()) {
      Matter.Body.setVelocity(b, { x: 0, y: 0 });
      Matter.Body.setAngularVelocity(b, 0);
    }
    // Reset interpolation state so the next render doesn't lerp across the
    // teleport (which would look like a fast slide back to spawn).
    this.snapshotPrev();
  }
}

/** Returns the signed shortest-arc delta from `a` to `b` in radians. */
function shortestAngleDelta(a: number, b: number): number {
  const TWO_PI = Math.PI * 2;
  let d = (b - a) % TWO_PI;
  if (d > Math.PI) d -= TWO_PI;
  else if (d < -Math.PI) d += TWO_PI;
  return d;
}
